/**
 * FilterSidebar.tsx
 *
 * The collapsible filter rail on list pages, and the same filters rendered inside a
 * sheet below `md`, where a 256px rail cannot coexist with a content column.
 *
 * ── Props that stop applying inside a sheet ─────────────────────────────────────
 * `collapsed`, `defaultCollapsed`, `onToggle` and the persisted collapse key are
 * **deliberate no-ops** off-canvas. They are not merely unused — a caller that sets
 * them gets no error and no effect, which is worth stating because the call site
 * shows no symptom. The reason: this panel defaults to collapsed and renders nothing
 * but a header when it is, so honouring the prop inside a sheet would open a drawer
 * onto a blank strip.
 *
 * What the sheet branch may change is bounded: disclosure state and layout chrome —
 * width, border, the collapse affordance — and never the set of filters or the data
 * they carry. Both branches render the same `body`, which is what keeps that true
 * rather than merely intended.
 */
import { CollapseChevron } from '@/components/ui/collapse-chevron';
import { Input } from '@/components/ui/Input';
import { ScrollArea } from '@/components/ui/ScrollArea';
import { cn } from '@/lib/utils';
import { FilterControls } from '@/components/filters/FilterControls';
import {
  type FilterConfig,
  type FilterNegations,
  type FilterOption,
  type FilterValue,
} from '@/types/filters.types';
import { Filter, Sparkles, Send, Loader2 } from 'lucide-react';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useInOffCanvas } from '@/components/layout/OffCanvasPanel';

interface FilterSidebarProps {
  filters: FilterConfig[];
  values: Record<string, FilterValue>;
  options: Record<string, FilterOption[]>;
  onChange: (filterKey: string, value: FilterValue) => void;
  onClear?: (filterKey: string) => void;
  negations?: FilterNegations;
  onNegateChange?: (filterKey: string, negate: boolean) => void;
  showAIAssistant?: boolean;
  onAIFilter?: (query: string) => void;
  aiLoading?: boolean;
  aiExplanation?: string;
  defaultCollapsed?: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
  searchCommitMode?: 'change' | 'blur-or-enter';
  storageKey?: string;
  lockedKeys?: string[];
  lockedLabel?: string;
}

export function FilterSidebar({
  filters,
  values,
  options,
  onChange,
  onClear,
  negations,
  onNegateChange,
  showAIAssistant = true,
  onAIFilter,
  aiLoading,
  aiExplanation,
  defaultCollapsed = true,
  collapsed: controlledCollapsed,
  onToggle,
  searchCommitMode = 'change',
  storageKey,
  lockedKeys,
  lockedLabel = 'Global',
}: FilterSidebarProps) {
  const [persistedCollapsed, setPersistedCollapsed] = useLocalStorage(
    storageKey ? `ftp-filter-collapsed-${storageKey}` : '__unused__',
    defaultCollapsed,
  );
  const [internalCollapsed, setInternalCollapsed] = useState(
    storageKey ? persistedCollapsed : defaultCollapsed,
  );
  const isCollapsed = controlledCollapsed ?? internalCollapsed;
  const toggle = onToggle ?? (() => {
    setInternalCollapsed((c) => {
      const next = !c;
      if (storageKey) setPersistedCollapsed(next);
      return next;
    });
  });

  const [aiQuery, setAIQuery] = useState('');
  const explanationTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [showExplanation, setShowExplanation] = useState(false);

  useEffect(() => {
    if (aiExplanation) {
      setShowExplanation(true);
      clearTimeout(explanationTimerRef.current);
      explanationTimerRef.current = setTimeout(() => setShowExplanation(false), 6000);
    }
    return () => clearTimeout(explanationTimerRef.current);
  }, [aiExplanation]);

  const handleAISubmit = useCallback(() => {
    const q = aiQuery.trim();
    if (!q || !onAIFilter || aiLoading) return;
    onAIFilter(q);
  }, [aiQuery, onAIFilter, aiLoading]);

  const inOffCanvas = useInOffCanvas();

  const body = (
    <div className="p-4 space-y-5">
      {showAIAssistant && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-blue-600" />
            <span className="text-xs font-medium">AI Filter Assistant</span>
          </div>
          <div className="relative">
            <Input
              placeholder="e.g., Female members over 50"
              className="h-8 text-xs pr-8"
              value={aiQuery}
              onChange={(e) => setAIQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAISubmit();
              }}
              disabled={aiLoading}
            />
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-600 hover:text-blue-500 disabled:text-blue-300"
              disabled={aiLoading || !aiQuery.trim()}
              onClick={handleAISubmit}
            >
              {aiLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
          {showExplanation && aiExplanation && (
            <p className="text-[10px] text-blue-600/80 leading-tight">
              {aiExplanation}
            </p>
          )}
        </div>
      )}

      <FilterControls
        filters={filters}
        values={values}
        options={options}
        onChange={onChange}
        onClear={onClear}
        negations={negations}
        onNegateChange={onNegateChange}
        searchCommitMode={searchCommitMode}
        storageKey={storageKey}
        lockedKeys={lockedKeys}
        lockedLabel={lockedLabel}
      />
    </div>
  );

  if (inOffCanvas) {
    // Auto height on purpose, not `h-full`: the sheet already owns the scroll
    // container, and pinning this to its height would clip a long filter list
    // instead of letting the sheet scroll it. The sheet supplies the background.
    return <div className="flex w-full flex-col text-slate-800">{body}</div>;
  }

  return (
    <div
      className={cn(
        'group relative flex-shrink-0 border-r border-slate-200 bg-white text-slate-800 transition-[width] duration-200 ease-linear',
        isCollapsed ? 'w-14' : 'w-64',
      )}
    >
      <div className="flex h-full w-full flex-col overflow-hidden">
        <div
          className={cn(
            'flex items-center gap-2 px-4 py-4 border-b border-slate-200',
            isCollapsed && 'justify-center',
          )}
        >
          <Filter className="h-4 w-4 text-blue-600 flex-shrink-0" />
          {!isCollapsed && (
            <span className="text-sm font-semibold">Filters</span>
          )}
        </div>

        {!isCollapsed && <ScrollArea className="flex-1 min-h-0">{body}</ScrollArea>}
      </div>

      <CollapseChevron
        collapsed={isCollapsed}
        onClick={toggle}
        side="left"
        title={isCollapsed ? 'Show filters' : 'Hide filters'}
      />
    </div>
  );
}
